import { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { ConsensoMarketing } from "../components/ConsensoMarketing";
import { Calendar } from "../components/ui/calendar";
import { CalendarDays } from "lucide-react";
import { useBrandFont } from "../lib/brandFonts";
import PaxInput from "../components/PaxInput";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const FIELD_INPUT_TYPE = { email: "email", data_nascita: "date" };

function todayPlus(days) {
  const d = new Date();
  d.setDate(d.getDate() + days);
  return d.toISOString().slice(0, 10);
}

function isoLocal(d) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

function fmtDataIt(iso) {
  try {
    return new Date(iso + "T12:00:00").toLocaleDateString("it-IT", { weekday: "long", day: "numeric", month: "long", year: "numeric" });
  } catch { return iso; }
}

export default function ModuloPubblico() {
  const { rid, slug } = useParams();
  const [cfg, setCfg] = useState(null);
  const [error, setError] = useState(null);
  const [date, setDate] = useState(todayPlus(0));
  const [slotsInfo, setSlotsInfo] = useState(null);
  const [time, setTime] = useState("");
  const [fields, setFields] = useState({});
  const [pax, setPax] = useState(2);
  const [calOpen, setCalOpen] = useState(false);
  const [notes, setNotes] = useState("");
  const [consent, setConsent] = useState(false);
  const [website, setWebsite] = useState("");
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(null);
  const [formError, setFormError] = useState(null);
  const offerTitle = new URLSearchParams(window.location.search).get("offer_title") || "";

  useEffect(() => {
    axios.get(`${API}/public/form/${rid}/m/${slug}`)
      .then((r) => { setCfg(r.data); setPax(r.data.form.pax_min || 1); })
      .catch((e) => setError(e.response?.data?.detail || "Modulo non disponibile"));
  }, [rid, slug]);

  const loadSlots = useCallback((d) => {
    setTime("");
    setSlotsInfo(null);
    axios.get(`${API}/public/form/${rid}/m/${slug}/slots`, { params: { date: d } })
      .then((r) => setSlotsInfo(r.data))
      .catch(() => setSlotsInfo({ aperto: false, slots: [], no_availability_message: "Errore di caricamento" }));
  }, [rid, slug]);

  useEffect(() => { if (cfg && cfg.form.tipo !== "coda") loadSlots(date); }, [cfg, date, loadSlots]);

  // Se il modulo ha periodi di validita', la data di default e' il primo giorno prenotabile
  useEffect(() => {
    const periods = cfg?.form?.periods || [];
    if (!periods.length) return;
    const maxG = cfg.form.max_giorni_futuri ?? 15;
    const t = new Date(); t.setHours(0, 0, 0, 0);
    for (let i = 0; i <= maxG; i++) {
      const d = new Date(t); d.setDate(d.getDate() + i);
      const iso = isoLocal(d);
      if (periods.some((p) => (p.da || "0000-00-00") <= iso && iso <= (p.a || "9999-12-31"))) {
        setDate(iso);
        break;
      }
    }
  }, [cfg]);

  useBrandFont(cfg?.brand?.font || "");

  if (error) {
    return (
      <div className="min-h-screen bg-zinc-100 flex items-center justify-center p-4" data-testid="mp-errore">
        <div className="bg-white rounded-xl p-6 text-center max-w-sm w-full">
          <div className="font-display font-bold text-zinc-900">Modulo non disponibile</div>
          <p className="text-sm text-zinc-500 mt-1">{error}</p>
        </div>
      </div>
    );
  }
  if (!cfg) return <div className="min-h-screen bg-zinc-100 flex items-center justify-center text-sm text-zinc-400">Caricamento...</div>;

  const { form, brand, coupon, promotion } = cfg;
  const primary = brand?.colors?.primary || "#01B2E0";
  const secondary = brand?.colors?.secondary || primary;
  const maxDate = todayPlus(form.max_giorni_futuri ?? 15);
  const overMaxPax = pax > form.pax_max;
  const periods = form.periods || [];
  const today0 = new Date(); today0.setHours(0, 0, 0, 0);
  const maxD = new Date(today0); maxD.setDate(maxD.getDate() + (form.max_giorni_futuri ?? 15));
  const dayValid = (d) => {
    if (d < today0 || d > maxD) return false;
    if (!periods.length) return true;
    const iso = isoLocal(d);
    return periods.some((p) => (p.da || "0000-00-00") <= iso && iso <= (p.a || "9999-12-31"));
  };
  const validDates = [];
  if (periods.length) {
    const d = new Date(today0);
    while (d <= maxD && validDates.length < 400) {
      if (dayValid(d)) validDates.push(isoLocal(d));
      d.setDate(d.getDate() + 1);
    }
  }
  const singleFixed = periods.length > 0 && validDates.length === 1;

  const submit = async (e) => {
    e.preventDefault();
    setFormError(null);
    const isCoda = form.tipo === "coda";
    if (!isCoda && !time) { setFormError("Seleziona un orario"); return; }
    setSending(true);
    try {
      const r = isCoda
        ? await axios.post(`${API}/public/form/${rid}/m/${slug}/queue-join`, {
            fields, party_size: pax, notes, consent, website,
          })
        : await axios.post(`${API}/public/form/${rid}/m/${slug}/book`, {
            fields, party_size: pax, date, time, notes, consent, website,
          });
      setDone(r.data);
    } catch (err) {
      setFormError(err.response?.data?.detail || "Invio non riuscito, riprova");
    } finally { setSending(false); }
  };

  const visibleSlots = (slotsInfo?.slots || []).filter((s) => !(s.full && slotsInfo.slot_full === "nascondi" && !slotsInfo.waitlist));

  if (done && done.token) {
    // Coda walk-in: posizione live, attesa stimata e link alla pagina di stato
    return (
      <div className="min-h-screen bg-zinc-100 flex items-start justify-center p-4 pt-10" data-testid="mp-coda-thankyou" data-brand-font="">
        <div className="bg-white rounded-xl p-6 max-w-md w-full text-center shadow-sm">
          {brand?.assets?.logo && <img src={brand.assets.logo} alt={brand.name} className="h-16 mx-auto mb-4 object-contain" />}
          <h1 className="font-display text-xl font-bold text-zinc-900">Sei in coda!</h1>
          <div className="mt-4">
            <div className="text-xs uppercase tracking-wider text-zinc-400 font-semibold">La tua posizione</div>
            <div className="font-display text-6xl font-extrabold mt-1" style={{ color: primary }} data-testid="mp-coda-position">{done.position}</div>
            <div className="text-sm text-zinc-500 mt-1" data-testid="mp-coda-ahead">
              {done.ahead === 0 ? "Sei il prossimo!" : `${done.ahead} ${done.ahead === 1 ? "gruppo" : "gruppi"} prima di te`}
            </div>
          </div>
          <div className="mt-4 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 text-sm text-amber-800" data-testid="mp-coda-wait">
            Attesa stimata: circa <b>{done.estimated_wait_min} minuti</b>
          </div>
          <a href={`/coda/${done.token}`} data-testid="mp-coda-status-link"
            className="mt-4 inline-block h-11 px-5 rounded-md text-white text-sm font-semibold leading-[2.75rem]"
            style={{ backgroundColor: primary }}>
            Segui la tua posizione in tempo reale
          </a>
        </div>
      </div>
    );
  }
  if (done) {
    const ty = done.thankyou || {};
    return (
      <div className="min-h-screen bg-zinc-100 flex items-start justify-center p-4 pt-10" data-testid="mp-thankyou" data-brand-font="">
        <div className="bg-white rounded-xl p-6 max-w-md w-full text-center shadow-sm">
          {brand?.assets?.logo && <img src={brand.assets.logo} alt={brand.name} className="h-16 mx-auto mb-4 object-contain" />}
          <h1 className="font-display text-xl font-bold text-zinc-900" data-testid="mp-ty-titolo">
            {ty.titolo || "Richiesta di prenotazione inviata con successo!"}
          </h1>
          {done.waitlisted && (
            <div className="mt-3 text-sm font-semibold text-sky-700 bg-sky-50 border border-sky-200 rounded-lg px-3 py-2" data-testid="mp-waitlisted">
              Lo slot era pieno: sei stato inserito in lista d'attesa.
            </div>
          )}
          {ty.descrizione && (
            <div className="text-sm text-zinc-600 mt-3 text-left [&_ul]:list-disc [&_ul]:pl-5 [&_a]:underline"
              dangerouslySetInnerHTML={{ __html: ty.descrizione }} data-testid="mp-ty-descrizione" />
          )}
          <div className="mt-4 bg-zinc-50 border border-zinc-200 rounded-lg p-3 text-sm text-zinc-700">
            {done.reservation.date} · {done.reservation.time} · {done.reservation.party_size} persone
          </div>
          {done.coupon_code && (
            <div className="mt-3 rounded-lg border-2 border-dashed p-3" style={{ borderColor: primary }} data-testid="mp-coupon">
              <div className="text-xs font-semibold text-zinc-500 uppercase">Il tuo coupon</div>
              <div className="font-mono font-bold text-lg text-zinc-900">{done.coupon_code}</div>
            </div>
          )}
          {done.public_token && (
            <a href={`/prenotazione/${done.public_token}`} data-testid="mp-gestisci"
              className="mt-4 inline-block h-11 px-5 rounded-md text-white text-sm font-semibold leading-[2.75rem]"
              style={{ backgroundColor: primary }}>
              Gestisci la tua prenotazione
            </a>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-zinc-100 py-6 px-4" data-testid="modulo-pubblico" data-brand-font="">
      <style>{`#modulo-pubblico .mp-day-ok { background:${primary}1f; font-weight:700; border-radius:6px; }
#modulo-pubblico .mp-selected { background:${primary} !important; color:#fff !important; border-radius:6px; }
#modulo-pubblico .rdp-cell:has(.mp-selected) { background:transparent !important; }
#modulo-pubblico .rdp { margin:0; --rdp-cell-size:38px; }`}</style>
      <div className="max-w-md mx-auto bg-white rounded-xl shadow-sm overflow-hidden">
        {brand?.assets?.cover && (
          <img src={brand.assets.cover} alt={brand?.name || "Copertina"}
            className="w-full h-40 object-cover" data-testid="mp-cover" />
        )}
        {form.landing?.immagine && form.landing?.immagine_mode !== "overlay" && (
          <img src={form.landing.immagine} alt={form.landing?.titolo || form.name}
            className={form.landing?.img_fit === "intera" ? "w-full h-auto block" : "w-full h-48 object-cover"}
            data-testid="mp-immagine" />
        )}
        {(() => { const overlayImg = form.landing?.immagine_mode === "overlay" && form.landing?.immagine; return (
        <div className="p-5 border-b border-zinc-100 text-center relative overflow-hidden"
          style={overlayImg ? {} : { background: `linear-gradient(135deg, ${primary}14, ${secondary}0d)` }}>
          {overlayImg && (<>
            <img src={form.landing.immagine} alt="" aria-hidden="true"
              className={`absolute inset-0 w-full h-full ${form.landing?.img_fit === "intera" ? "object-contain" : "object-cover"}`}
              data-testid="mp-immagine-overlay" />
            <div className="absolute inset-0" style={{ backgroundColor: "rgba(0,0,0,.55)" }} />
          </>)}
          <div className={overlayImg ? "relative" : ""}>
            {brand?.assets?.logo && <img src={brand.assets.logo} alt={brand.name} className="h-14 mx-auto mb-2 object-contain" />}
            <div className={`text-xs font-semibold uppercase tracking-wide ${overlayImg ? "text-white/80" : "text-zinc-500"}`}>{brand?.name}</div>
            <h1 className={`font-display text-2xl font-bold mt-1 ${overlayImg ? "text-white" : "text-zinc-900"}`} data-testid="mp-titolo">
              {form.landing?.titolo || form.name}
            </h1>
            {form.landing?.sottotitolo && <p className={`text-sm mt-1 ${overlayImg ? "text-white/85" : "text-zinc-500"}`} data-testid="mp-sottotitolo">{form.landing.sottotitolo}</p>}
          </div>
        </div>
        ); })()}

        <div className="p-5 space-y-5">
          {form.landing?.descrizione && (
            <div className="text-sm text-zinc-700 [&_ul]:list-disc [&_ul]:pl-5 [&_a]:underline"
              dangerouslySetInnerHTML={{ __html: form.landing.descrizione }} data-testid="mp-descrizione" />
          )}
          {(coupon || promotion) && (
            <div className="rounded-lg border p-3 text-sm" style={{ borderColor: `${primary}66`, backgroundColor: `${primary}0d` }} data-testid="mp-promo-box">
              {coupon && <div><span className="font-bold text-zinc-900">{coupon.name}</span>{coupon.descrizione ? <span className="text-zinc-600"> — {coupon.descrizione}</span> : null}</div>}
              {promotion && <div className="mt-1"><span className="font-bold text-zinc-900">{promotion.name}</span>{promotion.descrizione ? <span className="text-zinc-600"> — {promotion.descrizione}</span> : null}</div>}
            </div>
          )}

          {(form.landing?.cta_links || []).length > 0 && (
            <div className="space-y-2.5" data-testid="mp-cta-links">
              {form.landing.cta_links.map((cta, i) => (
                <a key={i} href={cta.url} data-testid={`mp-cta-${i}`}
                  className="w-full h-14 rounded-xl text-white font-bold text-base flex items-center justify-center text-center px-4 shadow-sm active:scale-[0.98] transition"
                  style={{ background: i === 0 ? `linear-gradient(135deg, ${primary}, ${secondary})` : "#18181b" }}>
                  {cta.label}
                </a>
              ))}
            </div>
          )}

          {offerTitle && !form.landing?.solo_landing && (
            <div className="rounded-lg border-2 p-3" style={{ borderColor: primary }} data-testid="mp-offerta-banner">
              <div className="text-[10px] font-bold uppercase tracking-wider text-zinc-500">Offerta selezionata</div>
              <div className="font-display font-bold text-sm text-zinc-900" data-testid="mp-offerta-titolo">{offerTitle}</div>
              <p className="text-[11px] text-zinc-500 mt-1">Il coupon ti verrà assegnato automaticamente dopo la prenotazione, in base al numero di persone.</p>
            </div>
          )}
          {!form.landing?.solo_landing && (
          <form onSubmit={submit} className="space-y-4">
            <input value={website} onChange={(e) => setWebsite(e.target.value)} tabIndex={-1} autoComplete="off"
              className="hidden" aria-hidden="true" />
            {form.tipo !== "coda" && (<>
            <div>
              <label className="block text-xs font-semibold uppercase tracking-wider text-zinc-500 mb-1">Data</label>
              {periods.length > 0 ? (
                singleFixed ? (
                  <div className="w-full h-11 px-3 rounded-md border border-zinc-200 text-sm bg-zinc-50 flex items-center gap-2 text-zinc-800 font-semibold" data-testid="mp-data-fissa">
                    <CalendarDays size={16} style={{ color: primary }} />
                    <span className="capitalize">{fmtDataIt(date)}</span>
                    <span className="ml-auto text-[10px] font-semibold uppercase tracking-wider text-zinc-400">data fissa</span>
                  </div>
                ) : (
                  <>
                    <button type="button" onClick={() => setCalOpen((o) => !o)} data-testid="mp-date-bar"
                      className="w-full h-11 px-3 rounded-md border border-zinc-300 text-sm bg-white flex items-center justify-between gap-2 text-zinc-800">
                      <span className="capitalize font-medium">{fmtDataIt(date)}</span>
                      <CalendarDays size={18} style={{ color: primary }} />
                    </button>
                    {calOpen && (
                      <div className="border border-zinc-200 rounded-md overflow-hidden flex justify-center py-2 mt-2" data-testid="mp-calendar">
                        <Calendar mode="single" selected={new Date(date + "T12:00:00")}
                          onSelect={(d) => { if (d) { setDate(isoLocal(d)); setCalOpen(false); } }}
                          defaultMonth={new Date(date + "T12:00:00")}
                          fromDate={today0} toDate={maxD}
                          disabled={(d) => !dayValid(d)}
                          modifiers={{ disponibile: (d) => dayValid(d) }}
                          modifiersClassNames={{ disponibile: "mp-day-ok" }}
                          classNames={{ day_selected: "mp-selected" }}
                          showOutsideDays={false} fixedWeeks />
                      </div>
                    )}
                    <p className="text-[11px] text-zinc-400 mt-1">Tocca la barra per scegliere tra le date prenotabili di questo modulo.</p>
                  </>
                )
              ) : (
                <input type="date" value={date} min={todayPlus(0)} max={maxDate} data-testid="mp-date"
                  onChange={(e) => setDate(e.target.value)}
                  className="w-full h-11 px-3 rounded-md border border-zinc-300 text-sm bg-white focus:outline-none focus:ring-2"
                  style={{ "--tw-ring-color": primary }} />
              )}
            </div>

            <div>
              <label className="block text-xs font-semibold uppercase tracking-wider text-zinc-500 mb-1">Orario</label>
              {!slotsInfo && <div className="text-xs text-zinc-400">Caricamento orari...</div>}
              {slotsInfo && !slotsInfo.aperto && (
                <div className="text-sm text-zinc-500 bg-zinc-50 border border-zinc-200 rounded-md px-3 py-2" data-testid="mp-no-avail">
                  {slotsInfo.no_availability_message}
                </div>
              )}
              {slotsInfo?.aperto && visibleSlots.length === 0 && (
                <div className="text-sm text-zinc-500 bg-zinc-50 border border-zinc-200 rounded-md px-3 py-2" data-testid="mp-no-avail">
                  {slotsInfo.no_availability_message}
                </div>
              )}
              {slotsInfo?.aperto && visibleSlots.length > 0 && (
                <div className="grid grid-cols-4 gap-1.5" data-testid="mp-slots">
                  {visibleSlots.map((s) => {
                    const disabled = s.full && !slotsInfo.waitlist;
                    return (
                      <button type="button" key={s.time} disabled={disabled}
                        onClick={() => setTime(s.time)} data-testid={`mp-slot-${s.time}`}
                        className={`h-10 rounded-md text-sm font-semibold border transition ${
                          time === s.time ? "text-white" : disabled ? "bg-zinc-100 text-zinc-300 border-zinc-200" :
                          s.full ? "bg-sky-50 text-sky-700 border-sky-300" : "bg-white text-zinc-700 border-zinc-300 hover:border-zinc-500"}`}
                        style={time === s.time ? { backgroundColor: primary, borderColor: primary } : {}}>
                        {s.time}{s.full && slotsInfo.waitlist ? "*" : ""}
                      </button>
                    );
                  })}
                </div>
              )}
              {slotsInfo?.waitlist && <p className="text-[11px] text-zinc-400 mt-1">* slot pieno: prenotazione in lista d'attesa</p>}
            </div>
            </>)}

            <div>
              <label className="block text-xs font-semibold uppercase tracking-wider text-zinc-500 mb-1">
                Persone (min {form.pax_min} — max {form.pax_max})
              </label>
              <PaxInput value={pax} onChange={setPax} min={form.pax_min}
                max={form.on_max_pax === "blocca" ? form.pax_max : 999} testid="mp-pax" />
              {overMaxPax && (
                <p className="text-xs text-amber-700 mt-1" data-testid="mp-pax-warning">
                  {form.max_pax_message || `Hai superato il limite massimo di ${form.pax_max} persone prenotabili con questo modulo. Contatta il ristorante.`}
                </p>
              )}
            </div>

            <div className="grid grid-cols-2 gap-2">
              {form.fields.map((f) => (
                <div key={f.key} className={["nome", "cognome"].includes(f.key) ? "" : "col-span-2"}>
                  <label className="block text-xs font-semibold uppercase tracking-wider text-zinc-500 mb-1">
                    {f.label}{f.required ? " *" : ""}
                  </label>
                  <input type={FIELD_INPUT_TYPE[f.key] || "text"} value={fields[f.key] || ""} required={f.required}
                    onChange={(e) => setFields({ ...fields, [f.key]: e.target.value })} data-testid={`mf-${f.key}`}
                    className="w-full h-11 px-3 rounded-md border border-zinc-300 text-sm bg-white focus:outline-none focus:ring-2" />
                </div>
              ))}
            </div>

            <div>
              <label className="block text-xs font-semibold uppercase tracking-wider text-zinc-500 mb-1">Note</label>
              <textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="mf-note"
                className="w-full px-3 py-2 rounded-md border border-zinc-300 text-sm bg-white focus:outline-none focus:ring-2" />
            </div>

            <ConsensoMarketing rid={rid} checked={consent} onChange={setConsent}
              testid="mf-consenso" accentColor={primary} />

            {formError && <div className="text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded-md px-3 py-2" data-testid="mp-error">{formError}</div>}

            <button type="submit" disabled={sending || (overMaxPax && form.on_max_pax === "blocca")} data-testid="mp-submit"
              className="w-full h-12 rounded-md text-white font-semibold text-sm active:scale-[0.98] transition disabled:opacity-50"
              style={{ backgroundColor: primary }}>
              {sending ? "Invio in corso..." : form.tipo === "coda" ? "Mettiti in coda" : "Prenota ora"}
            </button>
          </form>
          )}

          {form.landing?.condizioni && (
            <div className="text-[11px] text-zinc-500 border-t border-zinc-100 pt-3 [&_ul]:list-disc [&_ul]:pl-5 [&_a]:underline"
              dangerouslySetInnerHTML={{ __html: form.landing.condizioni }} data-testid="mp-condizioni" />
          )}
        </div>
        <p className="text-center text-[11px] text-zinc-400 mt-6" data-testid="mp-crosspromo">
          Prenotazione online con Dishenda ·{" "}
          <a href="/locali" className="underline hover:text-zinc-600 transition-colors">Scopri altri locali nella zona</a>
        </p>
      </div>
    </div>
  );
}
